import React, { useMemo } from "react";
import { useLanguage } from "../../../../../API/Context/LanguageContext";
import SkillsService from "../../../../../API/Services/SkillsService";
import SpecialtiesService from "../../../../../API/Services/SpecialtiesService";
import { VocabSkills } from "../../../../../ConfigFiles/multilingual/VocabSkills";
import { SpecialtySubSection } from "./Widgets/SpecialtySubSection";
import { SkillsBadgeSection } from "./Widgets/SkillsBadgeSection";
import type { SkillRead } from "../../../../../Types/schema-helpers";

export interface SkillMetadata {
  name: string;
  category: number;
}

export interface StepSkillsData {
  skills: {
    specialty_id: string | null;
    subspecialty_id: string | null;
    skills: string[];
  };
}

export interface StepSkillsMetadata {
  specialties: Record<string, { name: string }>;
  subspecialties: Record<string, { name: string }>;
  skills: Record<string, SkillMetadata>;
}

interface StepSkillsProps {
  data: StepSkillsData;
  update: (updatedData: Partial<StepSkillsData>) => void;
  metadata: StepSkillsMetadata;
  updateMeta: (category: keyof StepSkillsMetadata, newItems: Record<string, any>) => void;
}

const StepSkills: React.FC<StepSkillsProps> = ({ data, update, metadata, updateMeta }) => {
  const { t } = useLanguage();

  const skillsService = useMemo(() => new SkillsService(), []);
  const specService = useMemo(() => new SpecialtiesService(), []);

  const updateSkillsObject = (field: string, value: any) => {
    update({
      skills: {
        ...data.skills,
        [field]: value,
      },
    });
  };

  const activeSkillsList = data.skills?.skills || [];

  return (
    <div className="animate-in">
      <h4 className="mb-4 border-start border-primary border-4 ps-3">
        {t(VocabSkills, "skills_title")}
      </h4>

      <SpecialtySubSection
        specialtyId={data.skills.specialty_id}
        subspecialtyId={data.skills.subspecialty_id}
        specialtiesMeta={metadata.specialties}
        subspecialtiesMeta={metadata.subspecialties}
        specService={specService}
        onSelectSpecialty={(spec, isClear) => {
          if (isClear) {
            updateSkillsObject("specialty_id", null);
            return;
          }
          if (spec) {
            updateMeta("specialties", { [spec.id]: { name: spec.name } });
            update({
              skills: {
                ...data.skills,
                specialty_id: spec.id,
                subspecialty_id: null,
              },
            });
          }
        }}
        onSelectSubspecialty={(sub) => {
          if (!sub) {
            updateSkillsObject("subspecialty_id", null);
            return;
          }
          updateMeta("subspecialties", { [sub.id]: { name: sub.name } });
          updateSkillsObject("subspecialty_id", sub.id);
        }}
      />

      <hr />

      <SkillsBadgeSection
        activeSkills={activeSkillsList}
        skillsMeta={metadata.skills}
        skillsService={skillsService}
        onAddSkill={(skill: SkillRead) => {
          const skillId = skill.id;
          const categoryNum = Number(skill.category) || 0;

          updateMeta("skills", {
            [skillId]: {
              name: skill.name,
              category: categoryNum,
            },
          });

          if (!activeSkillsList.includes(skillId)) {
            updateSkillsObject("skills", [...activeSkillsList, skillId]);
          }
        }}
        onRemoveSkill={(skillId: string) => {
          updateSkillsObject(
            "skills",
            activeSkillsList.filter((sId) => sId !== skillId)
          );
        }}
      />
    </div>
  );
};

export default StepSkills;